import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import {
  So5LeaderboardType,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { Skeleton } from '@sorare/core/src/atoms/animations/Skeleton';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Skeleton as AutoSkeleton } from '@sorare/core/src/atoms/loader/Skeleton';
import {
  BrandS,
  DisplayL,
  DisplayXS,
  LabelM,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { FixtureChart } from '@sorare/core/src/components/player/FixtureChart';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { findThresholdColor } from '@sorare/core/src/lib/thresholds';

import { FAQ } from 'components/onboarding/FirstLineup/FirstLineupEdition/FAQ';

import type {
  lineupPastSimulatedResultsQuery,
  lineupPastSimulatedResultsQueryVariables,
} from './__generated__/index.graphql';
import logo from './assets/logo.svg';

const Root = styled(Vertical).attrs({ gap: 4 })`
  padding: var(--triple-unit) var(--double-and-a-half-unit) var(--double-unit);
  background-color: var(--c-black);
  border-radius: var(--triple-unit);
`;
const StyledSkeleton = styled(Skeleton)`
  width: 100%;
  height: 344px;
`;

const NUMBER_OF_PAST_GAME_WEEKS = 10;

const LINEUP_PAST_SIMULATED_RESULTS_QUERY = gql`
  query lineupPastSimulatedResultsQuery(
    $last: Int!
    $so5Appearances: [So5AppearanceInput!]!
    $so5LeaderboardType: So5LeaderboardType!
  ) {
    so5 {
      lineupPastSimulatedResults(
        last: $last
        so5Appearances: $so5Appearances
        so5LeaderboardType: $so5LeaderboardType
      ) {
        averageLineupScore
        simulatedResults {
          score
          so5Fixture {
            slug
            seasonGameWeek
          }
        }
      }
    }
  }
` as TypedDocumentNode<
  lineupPastSimulatedResultsQuery,
  lineupPastSimulatedResultsQueryVariables
>;

type Props = { cardSlugs: string[]; so5LeaderboardType: So5LeaderboardType };
export const TeamScoreBlock = ({ cardSlugs, so5LeaderboardType }: Props) => {
  const { data, loading } = useQuery(LINEUP_PAST_SIMULATED_RESULTS_QUERY, {
    variables: {
      last: NUMBER_OF_PAST_GAME_WEEKS,
      so5Appearances: cardSlugs.map(cardSlug => ({ cardSlug, captain: false })),
      so5LeaderboardType,
    },
  });

  const { lineupPastSimulatedResults } = data?.so5 || {};
  const { averageLineupScore, simulatedResults } =
    lineupPastSimulatedResults || {};

  const maxScore = simulatedResults?.reduce((acc, { score }) => {
    if (score > acc) {
      return score;
    }
    return acc;
  }, 0);
  const normalizedFixtureStats = simulatedResults?.map(result => {
    const { score, so5Fixture } = result;
    const { slug, seasonGameWeek } = so5Fixture;

    return {
      key: slug,
      games: [
        {
          id: 'game-id',
          playerScore: score,
          startDate: new Date(),
          tresholdColor: findThresholdColor(
            score,
            Sport.FOOTBALL,
            'TEAM_SCORE'
          ),
          gameLabel: (
            <LabelS color="var(--c-nd-400)" className="text-center">
              <FormattedMessage
                {...fantasy.gw}
                values={{
                  week: seasonGameWeek,
                }}
              />
            </LabelS>
          ),
          isHighlighted: true,
        },
      ],
    };
  });

  const displayedLineupScore = loading ? 400 : averageLineupScore;
  const showStats = (normalizedFixtureStats?.length || 0) >= 3;

  return (
    <Root>
      {!!showStats && (
        <>
          <Vertical gap={2}>
            <Horizontal gap={0.5}>
              <img src={logo} alt="" width={12} height={12} />
              <BrandS>
                <FormattedMessage
                  id="FirstLineupEdition.TeamScoreBlock.title"
                  defaultMessage="Average team score"
                />
              </BrandS>
            </Horizontal>
            <AutoSkeleton loading={loading}>
              <DisplayL>
                <FormattedNumber
                  value={displayedLineupScore || 0}
                  maximumFractionDigits={0}
                />
              </DisplayL>
            </AutoSkeleton>
          </Vertical>
          <Vertical gap={2}>
            <LabelM color="var(--c-nd-600)">
              <FormattedMessage
                id="FirstLineupEdition.TeamScoreBlock.secondSectionTitle"
                defaultMessage="Why does it matter?"
              />
            </LabelM>
            <DisplayXS>
              <FormattedMessage
                id="FirstLineupEdition.TeamScoreBlock.seconSectionText"
                defaultMessage="This is a good indicator for how well this team will perform next week."
              />
            </DisplayXS>
          </Vertical>
          <Vertical gap={2}>
            <LabelM color="var(--c-nd-600)">
              <FormattedMessage
                id="FirstLineupEdition.TeamScoreBlock.thirdSectionTitle"
                defaultMessage="Historical Performances"
              />
            </LabelM>
            {!!loading && <StyledSkeleton />}
            {!!normalizedFixtureStats && !loading && (
              <Vertical gap={1.5}>
                <FixtureChart
                  showBorder={false}
                  fixturesStats={normalizedFixtureStats}
                  yMaxValue={maxScore || 0}
                />
              </Vertical>
            )}
          </Vertical>
        </>
      )}
      <FAQ />
    </Root>
  );
};
